import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiClient, ENDPOINTS } from '../services/apiConfig';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid as MuiGrid,
  Button,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tab,
  Tabs,
  SelectChangeEvent,
  Avatar,
  List,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsightsIcon from '@mui/icons-material/Insights';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Emotion wheel data structure
const emotionWheel = {
  joy: {
    color: '#FFD166', // Bright yellow - represents happiness and positivity
    subEmotions: ['contentment', 'happiness', 'excitement', 'pride', 'optimism', 'love'],
  },
  sadness: {
    color: '#4A6FA5', // Medium blue - represents melancholy and sadness
    subEmotions: ['disappointment', 'grief', 'loneliness', 'melancholy', 'despair'],
  },
  fear: {
    color: '#073B4C', // Dark blue - represents depth and uncertainty
    subEmotions: ['anxiety', 'worry', 'nervousness', 'terror', 'insecurity'],
  },
  anger: {
    color: '#E63946', // Bright red - represents intensity and passion
    subEmotions: ['frustration', 'irritation', 'resentment', 'rage', 'annoyance'],
  },
  disgust: {
    color: '#6B8E23', // Olive green - represents aversion and distaste
    subEmotions: ['disapproval', 'dislike', 'revulsion', 'aversion'],
  },
  surprise: {
    color: '#9D4EDD', // Vibrant purple - represents wonder and unexpectedness
    subEmotions: ['amazement', 'astonishment', 'confusion', 'wonder'],
  },
  trust: {
    color: '#06D6A0', // Teal - represents reliability and stability
    subEmotions: ['trust', 'confidence', 'belonging', 'acceptance'],
  },
  Anticipation: {
    color: '#F9C74F', // Amber - represents expectation and alertness
    icon: '🤔',
    subEmotions: ['anticipation', 'eagerness', 'curiosity', 'interest'],
  },
  Love: {
    color: '#FF66B3', // Pink - represents affection and warmth
    icon: '😍',
    subEmotions: ['love', 'affection', 'romance', 'belonging'],
  },
  Guilt: {
    color: '#5C6D70', // Slate gray - represents heaviness and burden
    icon: '😔',
    subEmotions: ['guilt', 'shame', 'regret', 'disappointment'],
  },
  Shame: {
    color: '#7D5BA6', // Muted purple - represents withdrawal and hiding
    icon: '😠',
    subEmotions: ['shame', 'regret', 'disappointment'],
  },
  Pride: {
    color: '#F8961E', // Orange - represents confidence and achievement
    icon: '😊',
    subEmotions: ['pride', 'optimism', 'love'],
  },
  Relief: {
    color: '#90BE6D', // Light green - represents release and relaxation
    icon: '😌',
    subEmotions: ['relief', 'comfort', 'contentment'],
  },
  Curiosity: {
    color: '#43AA8B', // Turquoise - represents exploration and openness
    icon: '🤔',
    subEmotions: ['curiosity', 'interest', 'questioning', 'exploration'],
  },
  Contempt: {
    color: '#577590', // Steel blue - represents coldness and distance
    icon: '😒',
    subEmotions: ['contempt', 'disapproval', 'revulsion', 'aversion'],
  },  
  SelfPity: {
    color: '#9381FF', // Lavender - represents introspection
    icon: '😞',
    subEmotions: ['self-pity', 'self-doubt', 'self-hatred'],
  },
  vulnerability: {
    color: '#F4ACB7', // Soft pink - represents sensitivity and openness
    icon: '😔',
    subEmotions: ['vulnerability', 'inequality', 'inevitability'],
  },
  melancholy: {
    color: '#457B9D', // Muted blue - represents thoughtfulness and reflection
    icon: '😞',
    subEmotions: ['melancholy', 'despair'],
  },
  ecstasy: {
    color: '#F9C80E', // Bright gold - represents intense joy and celebration
    icon: '🤩',
    subEmotions: ['ecstasy', 'delight'],
  },
  dread: {
    color: '#2B2D42', // Dark navy - represents foreboding and heaviness
    icon: '😨',
    subEmotions: ['dread', 'fear'],
  },
  contentment: {
    color: '#A7C957', // Soft green - represents peace and satisfaction
    icon: '😊',
    subEmotions: ['contentment', 'happiness', 'excitement', 'pride', 'optimism', 'love'],
  },
  Awkwardness: {
    color: '#E07A5F', // Terracotta - represents discomfort and unease
    icon: '😳',
    subEmotions: ['awkwardness', 'uncomfortable', 'uneasy'],
  },
  Awe: {
    color: '#7209B7', // Deep purple - represents profound impact and wonder
    icon: '😮',
    subEmotions: ['awe', 'surprise', 'amazement'],
  },
  Apathy: {
    color: '#BCBCBC', // Gray - represents neutrality and detachment
    icon: '😐',
    subEmotions: ['apathy', 'disinterest', 'uninterest'],
  },
  Amusement: {
    color: '#FF9F1C', // Bright orange - represents playfulness and fun
    icon: '😄',
    subEmotions: ['amusement', 'delight', 'entertainment'],
  },
  Remorse: {
    color: '#5E6472', // Slate gray - represents regret and reflection
    icon: '😔',
    subEmotions: ['remorse', 'shame', 'regret', 'disappointment'],
  },
  Resentment: {
    color: '#B5838D', // Mauve - represents lingering negative feelings
    icon: '😠',
    subEmotions: ['resentment', 'bitterness', 'grudge', 'animosity'],
  },
};

// Factor options
const factorOptions = [
  'Work', 'Relationships', 'Family', 'Health', 'Exercise',
  'Sleep', 'Nutrition', 'Weather', 'Social Media', 'News',
  'Meditation', 'Hobbies', 'Finances', 'Travel', 'Learning',
  'Journaling', 'Creative Activities', 'Social Interaction'
];

// Update the MoodEntry interface
interface MoodEntry {
  id?: string | number; // Allow string for MongoDB _id
  _id?: string; // MongoDB _id
  date: string;
  primaryEmotion: string;
  subEmotion: string;
  score: number;
  factors: string[];
  notes: string;
  creativeActivity?: {
    completed: boolean;
    duration: number;
    type: string;
  };
}

function MoodTracker() {
  const [moodEntries, setMoodEntries] = useState<MoodEntry[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(location.state?.showInsights ? 1 : 0);
  const [selectedPrimaryEmotion, setSelectedPrimaryEmotion] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [weeklyInsights, setWeeklyInsights] = useState<string[]>([]);
  const [factorImpactData, setFactorImpactData] = useState<{ factor: string; average: number }[]>([]);

  // Cumulative pagination for Entries tab
  const [entriesPage, setEntriesPage] = useState(0);
  const entriesPerPage = 9;

  const sortedEntriesDesc = useMemo(
    () => [...moodEntries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [moodEntries]
  );

  const displayedEntries = useMemo(
    () => sortedEntriesDesc.slice(0, (entriesPage + 1) * entriesPerPage),
    [sortedEntriesDesc, entriesPage]
  );

  const hasNextPage = (entriesPage + 1) * entriesPerPage < sortedEntriesDesc.length;

  const handleSeeMore = () => {
    if (hasNextPage) {
      setEntriesPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    // Reset pagination when the entries list changes
    setEntriesPage(0);
  }, [moodEntries]);

  // Add a ref for the mood timeline section
  const timelineRef = useRef<HTMLDivElement>(null);

  // Form state
  const [newEntry, setNewEntry] = useState<Omit<MoodEntry, 'id'>>({
    date: new Date().toISOString().split('T')[0],
    primaryEmotion: '',
    subEmotion: '',
    score: 5,
    factors: [],
    notes: '',
  });

  // Add effect to scroll to timeline when fromDashboard is true
  useEffect(() => {
    if (location.state?.fromDashboard && timelineRef.current) {
      timelineRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.state]);

  // Fetch mood entries from the database
  const fetchMoodEntries = useCallback(async () => {
    try {
      setLoading(true);

      // Get token from localStorage
      const token = localStorage.getItem('token');

      if (!token) {
        // If no token, show error message
        setError('Please log in to view your mood entries');
        setLoading(false);
        return;
      }

      // Fetch mood entries from backend
      const response = await apiClient.get(ENDPOINTS.MOODS);

      if (response.data && response.data.length > 0) {
        setMoodEntries(response.data);
      } else {
        // If no entries found, set empty array
        setMoodEntries([]);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching mood entries:', err);
      setError('Failed to load mood entries. Please try again later.');
      setMoodEntries([]);
      setLoading(false);
    }
  }, []);

  // Use the fetchMoodEntries function in useEffect
  useEffect(() => {
    fetchMoodEntries();
  }, [fetchMoodEntries]);

  // Calculate factor impact data
  const calculateFactorImpact = useCallback(() => {
    if (moodEntries.length < 3) {
      return [] as { factor: string; average: number }[];
    }

    // Get entries from the last 30 days for analysis
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentEntries = moodEntries.filter((entry) => new Date(entry.date) >= thirtyDaysAgo);

    // Analyze factors correlation with mood scores
    const factorScores: Record<string, number[]> = {};

    recentEntries.forEach((entry) => {
      entry.factors.forEach((factor) => {
        if (!factorScores[factor]) {
          factorScores[factor] = [];
        }
        factorScores[factor].push(entry.score);
      });
    });

    // Calculate average score for each factor
    const factorAverages: Record<string, number> = {};

    Object.entries(factorScores).forEach(([factor, scores]) => {
      if (scores.length >= 2) {
        const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        factorAverages[factor] = average;
      }
    });

    // Sort factors by average score and get top 5
    return Object.entries(factorAverages)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([factor, average]) => ({
        factor,
        average: parseFloat(average.toFixed(1)),
      }));
  }, [moodEntries]);

  // Add this function to calculate emotion distribution
  const getEmotionDistribution = useCallback(() => {
    const distribution: Record<string, number> = {};

    moodEntries.forEach((entry) => {
      const emotion = entry.primaryEmotion;
      distribution[emotion] = (distribution[emotion] || 0) + 1;
    });

    return distribution;
  }, [moodEntries]);

  // Generate insights based on real mood data
  const generateInsights = useCallback(() => {
    if (moodEntries.length === 0) {
      setWeeklyInsights(['Start tracking your mood to see personalized insights']);
      return;
    }

    const insights: string[] = [];

    // Get entries from the last 30 days for analysis
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentEntries = moodEntries.filter((entry) => new Date(entry.date) >= thirtyDaysAgo);

    // Find most common primary emotion
    const emotionCounts: Record<string, number> = {};
    recentEntries.forEach((entry) => {
      emotionCounts[entry.primaryEmotion] = (emotionCounts[entry.primaryEmotion] || 0) + 1;
    });

    let mostCommonEmotion = '';
    let highestCount = 0;

    Object.entries(emotionCounts).forEach(([emotion, count]) => {
      if (count > highestCount) {
        mostCommonEmotion = emotion;
        highestCount = count;
      }
    });

    if (mostCommonEmotion) {
      const percentage = Math.round((highestCount / recentEntries.length) * 100);
      insights.push(`Your most frequent emotion is ${mostCommonEmotion} (${percentage}% of entries)`);
    }

    // Analyze factors correlation with mood scores
    const factorScores: Record<string, number[]> = {};

    recentEntries.forEach((entry) => {
      entry.factors.forEach((factor) => {
        if (!factorScores[factor]) {
          factorScores[factor] = [];
        }
        factorScores[factor].push(entry.score);
      });
    });

    // Find factors with highest average scores
    const factorAverages: Record<string, number> = {};

    Object.entries(factorScores).forEach(([factor, scores]) => {
      if (scores.length >= 3) {
        const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        factorAverages[factor] = average;
      }
    });

    const sortedFactors = Object.entries(factorAverages)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2);

    sortedFactors.forEach(([factor, average]) => {
      insights.push(`${factor} is associated with higher mood scores (avg: ${average.toFixed(1)})`);
    });

    // Find day of week patterns
    const dayScores: Record<string, number[]> = {
      Sunday: [],
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
    };

    recentEntries.forEach((entry) => {
      const day = new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long' });
      dayScores[day].push(entry.score);
    });

    const dayAverages: Record<string, number> = {};

    Object.entries(dayScores).forEach(([day, scores]) => {
      if (scores.length > 0) {
        const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        dayAverages[day] = average;
      }
    });

    const bestDay = Object.entries(dayAverages).sort((a, b) => b[1] - a[1])[0];

    const worstDay = Object.entries(dayAverages).sort((a, b) => a[1] - b[1])[0];

    if (bestDay && bestDay[1] > 0) {
      insights.push(`Your mood tends to be highest on ${bestDay[0]}s (avg: ${bestDay[1].toFixed(1)})`);
    }

    if (worstDay && worstDay[1] > 0 && worstDay[0] !== bestDay[0]) {
      insights.push(`Your mood tends to dip on ${worstDay[0]}s - consider scheduling self-care`);
    }

    // If we have creative activities data
    const entriesWithCreative = recentEntries.filter((entry) => entry.creativeActivity && entry.creativeActivity.completed);

    if (entriesWithCreative.length >= 3) {
      const creativeScores = entriesWithCreative.map((entry) => entry.score);
      const nonCreativeEntries = recentEntries.filter((entry) => !entry.creativeActivity || !entry.creativeActivity.completed);
      const nonCreativeScores = nonCreativeEntries.map((entry) => entry.score);

      if (creativeScores.length > 0 && nonCreativeScores.length > 0) {
        const creativeAvg = creativeScores.reduce((sum, score) => sum + score, 0) / creativeScores.length;
        const nonCreativeAvg = nonCreativeScores.reduce((sum, score) => sum + score, 0) / nonCreativeScores.length;

        const difference = creativeAvg - nonCreativeAvg;

        if (difference > 0.5) {
          insights.push(`Creative activities appear to boost your mood by ${difference.toFixed(1)} points on average`);
        }
      }
    }

    // Ensure we have at least some insights
    if (insights.length === 0) {
      insights.push('Continue tracking your mood to see more personalized insights');
    }

    setWeeklyInsights(insights);
  }, [moodEntries]);

  // Call generateInsights and calculateFactorImpact whenever mood entries change
  useEffect(() => {
    generateInsights();
    setFactorImpactData(calculateFactorImpact());
  }, [moodEntries, generateInsights, calculateFactorImpact]);

  // Chart data
  const chartData = {
    labels: [...moodEntries]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((entry) => entry.date),
    datasets: [
      {
        label: 'Mood Intensity',
        data: [...moodEntries]
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .map((entry) => entry.score),
        fill: true,
        backgroundColor: 'rgba(108, 141, 250, 0.2)',
        borderColor: '#6C8DFA',
        tension: 0.4,
        pointBackgroundColor: '#4A306D',
        pointBorderColor: '#fff',
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const index = context.dataIndex;
            return `${moodEntries[index].subEmotion}: ${context.raw}/10`;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        title: {
          display: true,
          text: 'Intensity',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const handleAddEntry = async () => {
    if (!newEntry.primaryEmotion || !newEntry.subEmotion) {
      alert('Please select an emotion');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        alert('You need to be logged in to save mood entries');
        return;
      }

      const moodData = {
        date: newEntry.date,
        primaryEmotion: newEntry.primaryEmotion,
        subEmotion: newEntry.subEmotion,
        score: newEntry.score,
        factors: newEntry.factors || [],
        notes: newEntry.notes || '',
        creativeActivity: {
          completed: newEntry.factors?.includes('Creative Activities') || false,
          duration: 0,
          type: '',
        },
      };

      await apiClient.post(ENDPOINTS.MOODS, moodData);

      await fetchMoodEntries();

      setShowAddForm(false);
      resetForm();

      alert('Mood entry saved successfully!');
    } catch (err: any) {
      console.error('Error saving mood entry:', err);

      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        alert(`Failed to save your mood entry: ${err.response.data?.message || 'Server error'}`);
      } else if (err.request) {
        console.error('No response received:', err.request);
        alert('Failed to save your mood entry: No response from server');
      } else {
        console.error('Error setting up request:', err.message);
        alert(`Failed to save your mood entry: ${err.message}`);
      }
    }
  };

  const resetForm = () => {
    setNewEntry({
      date: new Date().toISOString().split('T')[0],
      primaryEmotion: '',
      subEmotion: '',
      score: 5,
      factors: [],
      notes: '',
    });
    setSelectedPrimaryEmotion(null);
  };

  const handleDeleteEntry = async (id: string | number) => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Please log in to delete mood entries');
        return;
      }

      await apiClient.delete(`${ENDPOINTS.MOODS}/${id}`);

      setMoodEntries(
        moodEntries.filter((entry) => {
          return entry._id ? entry._id !== id : entry.id !== id;
        })
      );
    } catch (err) {
      console.error('Error deleting mood entry:', err);
      setError('Failed to delete the mood entry. Please try again.');
    }
  };

  const handleFactorChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string | string[];
    setNewEntry({
      ...newEntry,
      factors: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePrimaryEmotionSelect = (emotion: string) => {
    setSelectedPrimaryEmotion(emotion);
    setNewEntry({
      ...newEntry,
      primaryEmotion: emotion,
      subEmotion: '',
    });
  };

  const handleSubEmotionSelect = (subEmotion: string) => {
    setNewEntry({
      ...newEntry,
      subEmotion: subEmotion,
    });
  };

  const handleVoiceRecording = () => {
    setIsRecording(!isRecording);
    if (isRecording) {
      setIsRecording(false);
    } else {
      console.log('Starting voice recording...');
      setTimeout(() => {
        setNewEntry((prev) => ({
          ...prev,
          notes: prev.notes + ' I recorded this with my voice.',
        }));
        setIsRecording(false);
      }, 3000);
    }
  };

  // Get emotion color
  const getEmotionColor = (emotion: string) => {
    return emotionWheel[emotion as keyof typeof emotionWheel]?.color || '#6C8DFA';
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
          Mood Tracker
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#4A306D', opacity: 0.8, fontFamily: '"Lato", sans-serif' }}>
          Track your emotions to gain insights into your emotional patterns <br /><br />
        </Typography>

        {/* Mood Timeline */}
        <Paper
          ref={timelineRef}
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 400, fontFamily: '"Lato", sans-serif' }}>
              Mood Timeline
            </Typography>
            <Button variant="text" endIcon={<TrendingUpIcon />} sx={{ color: '#6C8DFA' }}>
              Filter
            </Button>
          </Box>
          <Box sx={{ height: { xs: 180, sm: 220, md: 250 }, p: { xs: 0.5, md: 1 } }}>
            <Line data={chartData} options={chartOptions} />
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 400, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
            {showAddForm ? 'How are you feeling today?' : 'Your Mood Entries'}
          </Typography>
          <Button
            variant="contained"
            startIcon={showAddForm ? null : <AddIcon />}
            onClick={() => setShowAddForm(!showAddForm)}
            sx={{
              bgcolor: showAddForm ? 'rgba(0,0,0,0.1)' : 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
              color: showAddForm ? 'text.primary' : 'white',
              '&:hover': {
                bgcolor: showAddForm ? 'rgba(0,0,0,0.15)' : '#5A7DE0',
              },
              boxShadow: 'none',
              borderRadius: 2,
            }}
          >
            {showAddForm ? 'Cancel' : 'Log New Mood'}
          </Button>
        </Box>

        {showAddForm && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Emotion Wheel */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', fontFamily: '"Quicksand", sans-serif' }}>
              Select your primary emotion
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, justifyContent: 'center' }}>
              {Object.keys(emotionWheel).map((emotion) => (
                <Button
                  key={emotion}
                  onClick={() => handlePrimaryEmotionSelect(emotion)}
                  sx={{
                    width: { xs: 72, sm: 90, md: 100 },
                    height: { xs: 72, sm: 90, md: 100 },
                    borderRadius: '50%',
                    bgcolor:
                      selectedPrimaryEmotion === emotion ? `${getEmotionColor(emotion)}` : `${getEmotionColor(emotion)}40`,
                    color: selectedPrimaryEmotion === emotion ? 'white' : 'text.primary',
                    textTransform: 'capitalize',
                    '&:hover': {
                      bgcolor: `${getEmotionColor(emotion)}80`,
                    },
                    transition: 'all 0.3s ease',
                    transform: selectedPrimaryEmotion === emotion ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: selectedPrimaryEmotion === emotion ? 3 : 0,
                  }}
                >
                  {emotion}
                </Button>
              ))}
            </Box>

            {/* Sub-emotions */}
            {selectedPrimaryEmotion && (
              <>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', mt: 2, fontFamily: '"Quicksand", sans-serif' }}>
                  Select a more specific emotion
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {emotionWheel[selectedPrimaryEmotion as keyof typeof emotionWheel].subEmotions.map((subEmotion) => (
                    <Chip
                      key={subEmotion}
                      label={subEmotion}
                      onClick={() => handleSubEmotionSelect(subEmotion)}
                      sx={{
                        bgcolor: newEntry.subEmotion === subEmotion ? getEmotionColor(selectedPrimaryEmotion) : 'transparent',
                        color: newEntry.subEmotion === subEmotion ? 'white' : 'text.primary',
                        border: `1px solid ${getEmotionColor(selectedPrimaryEmotion)}`,
                        textTransform: 'capitalize',
                        p: 1,
                        '&:hover': {
                          bgcolor: `${getEmotionColor(selectedPrimaryEmotion)}40`,
                        },
                      }}
                    />
                  ))}
                </Box>
              </>
            )}

            {/* Intensity Slider */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', mt: 2 }}>
              How intense is this feeling? (1-10)
            </Typography>
            <Box sx={{ px: 2, mb: 3 }}>
              <Slider
                value={newEntry.score}
                onChange={(e, newValue) => setNewEntry({ ...newEntry, score: newValue as number })}
                min={1}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                sx={{
                  color: selectedPrimaryEmotion ? getEmotionColor(selectedPrimaryEmotion) : '#6C8DFA',
                  '& .MuiSlider-thumb': {
                    height: 24,
                    width: 24,
                    backgroundColor: '#fff',
                    border: `2px solid ${selectedPrimaryEmotion ? getEmotionColor(selectedPrimaryEmotion) : '#6C8DFA'}`,
                    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                      boxShadow: `0px 0px 0px 8px ${selectedPrimaryEmotion ? `${getEmotionColor(selectedPrimaryEmotion)}30` : 'rgba(108, 141, 250, 0.3)'}`,
                    },
                  },
                  '& .MuiSlider-valueLabel': {
                    backgroundColor: selectedPrimaryEmotion ? getEmotionColor(selectedPrimaryEmotion) : '#6C8DFA',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                  Mild
                </Typography>
                <Typography variant="caption" color="text.secondary">Moderate</Typography>
                <Typography variant="caption" color="text.secondary">Intense</Typography>
              </Box>
            </Box>

            {/* Contributing Factors */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', mt: 2 }}>
              What factors might be contributing to this feeling?
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Contributing Factors</InputLabel>
              <Select
                multiple
                value={newEntry.factors}
                onChange={handleFactorChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {factorOptions.map((factor) => (
                  <MenuItem key={factor} value={factor}>
                    {factor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Journal Lite */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', mt: 2 }}>
              Add notes (optional)
            </Typography>
            <Box sx={{ position: 'relative', mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                placeholder="How are you feeling in more detail? What's on your mind?"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  color: isRecording ? '#FF9F7E' : 'text.secondary',
                  animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(255, 159, 126, 0.7)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(255, 159, 126, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(255, 159, 126, 0)',
                    },
                  },
                }}
                onClick={handleVoiceRecording}
              >
                <MicIcon />
              </IconButton>
            </Box>

            <Button
              variant="contained"
              onClick={handleAddEntry}
              disabled={!newEntry.primaryEmotion || !newEntry.subEmotion}
              sx={{
                background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
                color: 'white',
                borderRadius: 2,
                py: 1.2,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 1,
                  background: 'linear-gradient(135deg, #5A7DE0 0%, #E58E6E 100%)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              Save Entry
            </Button>
          </Paper>
        )}

        {!showAddForm && (
          <div>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4A306D',
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: '#4A306D',
                  },
                }}
              >
                <Tab label="Entries" />
                <Tab label="Insights" />
              </Tabs>
            </Box>

            {/* Entries Tab */}
            {currentTab === 0 && (
              <Box sx={{ mt: 2 }}>
                {moodEntries.length === 0 ? (
                  <Typography variant="body1" sx={{ textAlign: 'center', py: 4, fontFamily: '"Quicksand", sans-serif' }}>
                    No mood entries yet. Start tracking your mood to see your history here.
                  </Typography>
                ) : (
                  <>
                    <MuiGrid container spacing={{ xs: 2, md: 3 }}>
                      {displayedEntries.map((entry) => (
                        <MuiGrid item xs={12} sm={6} md={4} key={entry.id ?? entry._id ?? `${entry.date}-${entry.subEmotion}`}>
                          <Card
                            elevation={0}
                            sx={{
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                                borderColor: getEmotionColor(entry.primaryEmotion),
                              },
                            }}
                          >
                            <Box
                              sx={{
                                height: 8,
                                bgcolor: getEmotionColor(entry.primaryEmotion),
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                              }}
                            />
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{
                                      bgcolor: `${getEmotionColor(entry.primaryEmotion)}40`,
                                      color: getEmotionColor(entry.primaryEmotion),
                                      width: 40,
                                      height: 40,
                                      mr: 1,
                                    }}
                                  >
                                    {entry.score}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="h6" sx={{ textTransform: 'capitalize', fontFamily: '"Lato", sans-serif' }}>
                                      {entry.subEmotion}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize', fontFamily: '"Quicksand", sans-serif' }}>
                                      {entry.primaryEmotion}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const entryId = entry._id || entry.id;
                                      if (entryId) {
                                        handleDeleteEntry(entryId);
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: '"Quicksand", sans-serif' }}>
                                {new Date(entry.date).toLocaleDateString('en-AU', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </Typography>

                              {entry.factors.length > 0 && (
                                <>
                                  <Divider sx={{ my: 1.5 }} />
                                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                                    Factors:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {entry.factors.map((factor, index) => (
                                      <Chip
                                        key={index}
                                        label={factor}
                                        size="small"
                                        sx={{
                                          bgcolor: 'rgba(108, 141, 250, 0.1)',
                                          borderRadius: 1,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </>
                              )}

                              {entry.notes && (
                                <>
                                  <Divider sx={{ my: 1.5 }} />
                                  <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Notes:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                                    {entry.notes}
                                  </Typography>
                                </>
                              )}
                            </CardContent>
                          </Card>
                        </MuiGrid>
                      ))}
                    </MuiGrid>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button variant="outlined" onClick={handleSeeMore} disabled={!hasNextPage} sx={{ borderColor: '#6C8DFA', color: '#6C8DFA' }}>
                        See More
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Insights Tab */}
            {currentTab === 1 && (
              <Box sx={{ mt: 2 }}>
                <MuiGrid container spacing={3}>
                  {/* First row - Weekly insights */}
                  <MuiGrid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        background: 'linear-gradient(135deg, rgba(108, 141, 250, 0.1) 0%, rgba(255, 159, 126, 0.1) 100%)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InsightsIcon sx={{ color: '#4A306D', mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 400, color: '#4A306D' }}>
                          Weekly Insights
                        </Typography>
                      </Box>

                      <List sx={{ '& .MuiListItem-root': { px: 1 } }}>
                        {weeklyInsights.map((insight, index) => (
                          <React.Fragment key={index}>
                            <Box component="li" sx={{ py: 1, display: 'flex' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#6C8DFA',
                                  mr: 2,
                                  mt: 0.5,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body1" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                                {insight}
                              </Typography>
                            </Box>
                            {index < weeklyInsights.length - 1 && <Divider variant="inset" component="li" />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  </MuiGrid>

                  {/* Second row - Emotion Distribution and Mood Factors Impact side by side */}
                  <MuiGrid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                      }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 400, color: '#4A306D' }}>
                        Emotion Distribution
                      </Typography>
                      <Box sx={{ height: 250, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {moodEntries.length > 0 ? (
                          <Box sx={{ width: '100%' }}>
                            {Object.entries(getEmotionDistribution())
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 5)
                              .map(([emotion, count], index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {emotion}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {Math.round((count / moodEntries.length) * 100)}%
                                    </Typography>
                                  </Box>
                                  <Box sx={{ width: '100%', bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, height: 10 }}>
                                    <Box
                                      sx={{
                                        height: '100%',
                                        borderRadius: 1,
                                        width: `${(count / moodEntries.length) * 100}%`,
                                        bgcolor: emotionWheel[emotion as keyof typeof emotionWheel]?.color || '#6C8DFA',
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                            Start tracking your mood to see your emotion distribution.
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </MuiGrid>

                  <MuiGrid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                      }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 400, color: '#4A306D' }}>
                        Mood Factors Impact
                      </Typography>
                      <Box sx={{ height: 250, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        {factorImpactData.length > 0 ? (
                          <Box sx={{ width: '100%' }}>
                            {factorImpactData.map((item, index) => (
                              <Box key={index} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {item.factor}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {item.average}/10
                                  </Typography>
                                </Box>
                                <Box sx={{ width: '100%', bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, height: 10 }}>
                                  <Box
                                    sx={{
                                      height: '100%',
                                      borderRadius: 1,
                                      width: `${(item.average / 10) * 100}%`,
                                      background: `linear-gradient(90deg, ${item.average > 5 ? '#06D6A0' : '#E63946'} 0%, ${item.average > 5 ? '#90BE6D' : '#FF9F1C'} 100%)`,
                                    }}
                                  />
                                </Box>
                              </Box>
                            ))}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Factors with higher scores positively impact your mood
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                            Continue tracking your mood with different factors to see their impact on your emotions.
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </MuiGrid>
                </MuiGrid>
              </Box>
            )}
          </div>
        )}
      </Container>
    </>
  );
}

export default MoodTracker;